/**
 * Utility to generate calendar subscription links for webinar/memorial events.
 *
 * Production-safe: all Date operations are guarded against Invalid Date so
 * that a bad date string never throws a RangeError that would bubble up to
 * the ErrorBoundary.
 */

const ONE_HOUR_MS = 60 * 60 * 1000;

/**
 * Attempt to parse a date string into a valid Date object.
 * Returns null if the string is missing or produces an Invalid Date.
 */
const safeParse = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

/** Format a Date as a compact UTC string for iCal / Google Calendar. */
const formatUTC = (d) => d.toISOString().replace(/[-:]|\.\d+/g, '');

export const generateCalendarLinks = (event = {}) => {
  const { title = 'Event', date = '', location = '', description = '' } = event;

  const startDate = safeParse(date) ?? new Date();          // fallback: now
  const endDate   = new Date(startDate.getTime() + ONE_HOUR_MS);

  const titleEnc  = encodeURIComponent(title);
  const descEnc   = encodeURIComponent(description);
  const locEnc    = encodeURIComponent(location);

  const googleUrl =
    `https://www.google.com/calendar/render?action=TEMPLATE` +
    `&text=${titleEnc}` +
    `&dates=${formatUTC(startDate)}/${formatUTC(endDate)}` +
    `&details=${descEnc}` +
    `&location=${locEnc}` +
    `&sf=true&output=xml`;

  const outlookUrl =
    `https://outlook.live.com/calendar/0/deeplink/compose` +
    `?subject=${titleEnc}` +
    `&startdt=${startDate.toISOString()}` +
    `&enddt=${endDate.toISOString()}` +
    `&body=${descEnc}` +
    `&location=${locEnc}`;

  const icalData = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//OneFinalLegacy//Webinar//EN',
    'BEGIN:VEVENT',
    `DTSTART:${formatUTC(startDate)}`,
    `DTEND:${formatUTC(endDate)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
    `LOCATION:${location}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');

  return { googleUrl, outlookUrl, icalData };
};